import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { ConfirmDialog, Toast, useToast, useConfirm } from '../components/ConfirmDialog';
import { MdPeople, MdEmail, MdPerson, MdAdminPanelSettings, MdSearch, MdFilterList, MdEdit, MdDelete, MdAdd, MdBadge } from 'react-icons/md';
import { AuthContext } from '../context/AuthContext';
import './Users.css';

const Users = () => {
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();
  const { dialog, confirm } = useConfirm();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'member',
    job_title: ''
  });

  useEffect(() => {
    // Redirect non-admin users to dashboard
    if (currentUser && currentUser.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [currentUser, navigate]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users');
      setUsers(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`/api/users/${editingUser.id}`, { full_name: formData.full_name, role: formData.role, job_title: formData.job_title });
      } else {
        await axios.post('/api/users', formData);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({ username: '', email: '', password: '', full_name: '', role: 'member', job_title: '' });
      addToast(editingUser ? 'User updated successfully' : 'User created successfully', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      addToast(error.response?.data?.error || 'Failed to save user', 'error');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, email: user.email, password: '', full_name: user.full_name || '', role: user.role, job_title: user.job_title || '' });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (userId === currentUser.id) {
      addToast('You cannot delete your own account!', 'error');
      return;
    }
    const ok = await confirm('Delete User', 'Are you sure you want to delete this user? This action cannot be undone.', { confirmText: 'Delete', confirmColor: '#EF4444' });
    if (!ok) return;
    try {
      await axios.delete(`/api/users/${userId}`);
      addToast('User deleted successfully', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      addToast(error.response?.data?.error || 'Failed to delete user', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'member',
      job_title: ''
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return '#EF4444';
      case 'manager':
        return '#F59E0B';
      case 'member':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isAdmin = currentUser?.role === 'admin';

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content users-main">
          <div className="users-page">
            <div className="page-header">
              <div className="header-content">
                <h1><MdPeople /> Users</h1>
              </div>
            </div>
            <div className="skeleton-grid">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-avatar"></div>
                  <div className="skeleton-text"></div>
                  <div className="skeleton-text short"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <Toast toasts={toasts} removeToast={removeToast} />
      <ConfirmDialog {...dialog} />
      <div className="main-content users-main">
        <div className="users-page">
          <div className="page-header">
            <div className="header-content">
              <h1><MdPeople /> Users</h1>
              <p className="header-subtitle">Manage team members and their roles</p>
            </div>
            <div className="header-actions">
              <span className="count-badge">{filteredUsers.length} {filteredUsers.length === 1 ? 'User' : 'Users'}</span>
              {isAdmin && (
                <button className="btn-primary" onClick={() => setShowModal(true)}>
                  <MdAdd /> Add User
                </button>
              )}
            </div>
          </div>

          <div className="filters-bar">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users by name, email, or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <MdFilterList className="filter-icon" />
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="member">Member</option>
              </select>
            </div>
          </div>

          {error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn-primary" onClick={fetchUsers}>Retry</button>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              <MdPeople className="empty-icon" />
              <p>{searchTerm || roleFilter !== 'all' ? 'No users match your filters' : 'No users found'}</p>
            </div>
          ) : (
            <div className="users-grid">
              {filteredUsers.map(user => (
                <div key={user.id} className="user-card">
                  {isAdmin && (
                    <div className="user-card-actions">
                      <button className="icon-btn edit" onClick={() => handleEdit(user)} title="Edit">
                        <MdEdit />
                      </button>
                      {user.id !== currentUser.id && (
                        <button className="icon-btn delete" onClick={() => handleDelete(user.id)} title="Delete">
                          <MdDelete />
                        </button>
                      )}
                    </div>
                  )}
                  
                  <div className="user-avatar">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.full_name || user.username} />
                    ) : (
                      <div className="avatar-placeholder" style={{ background: `linear-gradient(135deg, ${getRoleColor(user.role)}, ${getRoleColor(user.role)}dd)` }}>
                        {getInitials(user.full_name || user.username)}
                      </div>
                    )}
                  </div>
                  
                  <div className="user-info">
                    <h3>{user.full_name || user.username}</h3>
                    <p className="username">@{user.username}</p>
                  </div>

                  <div className="user-details">
                    <div className="detail-item">
                      <MdEmail className="detail-icon" />
                      <span>{user.email}</span>
                    </div>
                    {user.job_title && (
                      <div className="detail-item">
                        <MdBadge className="detail-icon" />
                        <span className="job-title">{user.job_title}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <MdAdminPanelSettings className="detail-icon" />
                      <span className="role-badge" style={{ background: `${getRoleColor(user.role)}20`, color: getRoleColor(user.role) }}>
                        {user.role}
                      </span>
                    </div>
                  </div>

                  <div className="user-footer">
                    <span className="joined-date">
                      <MdPerson className="footer-icon" />
                      Joined {formatDate(user.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
                <form onSubmit={handleSubmit}>
                  {!editingUser && (
                    <>
                      <div className="form-group">
                        <label>Username</label>
                        <input
                          type="text"
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Password</label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required
                          minLength="6"
                        />
                      </div>
                    </>
                  )}
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Job Title</label>
                    <input
                      type="text"
                      value={formData.job_title}
                      onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                      placeholder="e.g., Sales Manager, Social Media Manager"
                    />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="member">Member</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingUser ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Users;
